import React from "react";
import { cn } from "utils/cn";
import { SLEEK_CONTROL } from "pages/DesignCanvasAgentPage/components/sleek";
import type { SleekSize } from "./SleekPicker";

/**
 * A toolbar's on/off button in the Trends pages' finish (2026-10-26: Favorites, Shared with me, Select multiple …): a
 * pill in the controls' finish, on ink while it is on — the Trends rows' picked pill. `sm` sits beside the small search
 * and pickers of every page's toolbar.
 */
export const SleekToggle = ({
  active,
  onClick,
  icon,
  label,
  size = "sm",
  className,
  ...rest
}: {
  /** On/off; left out, it is a plain action in the same finish (Import, Export …) — never on ink. */
  active?: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  size?: SleekSize;
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "className">) => (
  <button
    type="button"
    {...(active !== undefined && { "aria-pressed": active })}
    onClick={onClick}
    className={cn(
      "sleek-press flex flex-none items-center whitespace-nowrap rounded-full font-medium transition-colors",
      size === "sm" ? "h-9 gap-1.5 text-[13px]" : "h-12 gap-2 text-[15px]",
      label ? (size === "sm" ? "px-3.5" : "px-5") : size === "sm" ? "w-9 justify-center" : "w-12 justify-center",
      active ? "border border-trueBlack bg-trueBlack text-trueWhite dark:border-trueWhite dark:bg-trueWhite dark:text-trueBlack" : cn(SLEEK_CONTROL, "text-trueBlack dark:text-white"),
      className
    )}
    {...rest}
  >
    {icon}
    {label}
  </button>
);

/**
 * A choice between a few views as one pill of icon buttons (grid / list), the picked one on ink — the toggle's finish.
 */
export const SleekSegmented = <T extends string>({
  options,
  value,
  onChange,
  size = "sm",
}: {
  options: { id: T; icon: React.ReactNode; label: string }[];
  value: T;
  onChange: (id: T) => void;
  size?: SleekSize;
}) => (
  <div role="radiogroup" className={cn(SLEEK_CONTROL, "flex flex-none items-center gap-0.5 rounded-full p-[3px]", size === "sm" ? "h-9" : "h-12")}>
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        role="radio"
        aria-checked={value === option.id}
        aria-label={option.label}
        title={option.label}
        onClick={() => onChange(option.id)}
        className={cn(
          "flex h-full items-center justify-center rounded-full transition-colors",
          size === "sm" ? "w-8" : "w-10",
          value === option.id ? "bg-trueBlack text-trueWhite dark:bg-trueWhite dark:text-trueBlack" : "text-grey hover:text-trueBlack dark:hover:text-white"
        )}
      >
        {option.icon}
      </button>
    ))}
  </div>
);
